// Copyright (c) 2025 Audere, All Rights Reserved

import React, { useState, useCallback, useEffect, useRef } from "react";
import { generatePath, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  Menu,
  MenuItem,
  Tooltip,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ClearSearchIcon from "@mui/icons-material/Cancel";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import SearchInput from "./SearchInput";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import { selectCurrentOrganizationId } from "../features/auth/authSlice";
import ProjectListMenu from "./ProjectListMenu";
import folderOpenUrl from "../../assets/folder_open.svg";
import {
  PROJECT_FLOW_QUERY_KEYS,
  PROJECT_FLOW_STEP_VALUES,
  type DashboardMenuOption,
  type DashboardSidebarProps,
  type ProjectData,
  type TestData,
} from "../types/projects";
import TestListsItem from "./TestListsItem";
import {
  CREATE_PROJECT_WITHOUT_TEST_PATH,
  DASHBOARD_PATH,
  MANAGE_SDK_PATH,
  UPDATE_PROJECT_WITHOUT_TEST_PATH,
} from "../utils/constants";
import { useAppDispatch } from "../features/store";
import { clearProject } from "../features/data/projectSlice";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

const DashboardSidebar = (props: DashboardSidebarProps) => {
  const {
    onSelectRDTTest,
    isBelowTabletSize,
    isDrawerOpen,
    onDrawerToggle,
    getProjectData,
    selectedTest,
    setSelectedTest,
    selectedProject,
    setSelectedProject,
    listProjectsData,
  } = props;
  const { t } = useTranslation("DashboardSidebar");
  const menuOptions = t("menuOptions", { returnObjects: true }) as string[];
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const dispatch = useAppDispatch();

  const [searchValue, setSearchValue] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    if (onDrawerToggle) {
      onDrawerToggle();
    }
    setIsCollapsed(prev => !prev);
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  const onSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(target.value);
  };
  const handleSearchClearClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleNewProject = useCallback(() => {
    if (currentOrganizationId) {
      dispatch(clearProject());
      const searchParams = new URLSearchParams();
      searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.PROJECT_NAME);
      const path = generatePath(CREATE_PROJECT_WITHOUT_TEST_PATH, {
        orgUid: currentOrganizationId,
      });
      navigate(`${path}?${searchParams.toString()}`);
    }
  }, [navigate, currentOrganizationId, dispatch]);

  const handleSelectProject = (project: ProjectData) => {
    setSelectedProject(project);
  };
  const handleSelectTest = useCallback(
    (test: TestData) => {
      setSelectedTest(test);
      if (getProjectData) {
        onSelectRDTTest(test, getProjectData);
      }
      if (selectedProject?.project_uid) {
        navigate(
          generatePath(DASHBOARD_PATH, {
            orgUid: currentOrganizationId,
            projectUid: selectedProject.project_uid,
            testUid: test.rdt_uid,
          })
        );
      }
    },
    [
      setSelectedTest,
      onSelectRDTTest,
      getProjectData,
      selectedProject?.project_uid,
      navigate,
      currentOrganizationId,
    ]
  );
  const handleMenuOptionClick = useCallback(
    (menu: DashboardMenuOption) => {
      if (!selectedProject?.project_uid) return;

      const actions: Record<DashboardMenuOption, () => void> = {
        "Manage SDK": () => {
          navigate(
            generatePath(MANAGE_SDK_PATH, {
              orgUid: currentOrganizationId,
              projectUid: selectedProject.project_uid,
            }),
            {
              state: {
                projectName: selectedProject.name,
                projectUid: selectedProject.project_uid,
                testUid: selectedTest?.rdt_uid,
              },
            }
          );
        },
        "Edit project": () => {
          dispatch(clearProject());
          navigate(
            generatePath(UPDATE_PROJECT_WITHOUT_TEST_PATH, {
              orgUid: currentOrganizationId,
              projectUid: selectedProject.project_uid,
            })
          );
        },
      };
      actions[menu]?.();
    },
    [navigate, currentOrganizationId, selectedProject, selectedTest, dispatch]
  );

  useEffect(() => {
    if (isBelowTabletSize) {
      setIsCollapsed(!isDrawerOpen);
    }
  }, [isBelowTabletSize, isDrawerOpen]);

  return (
    <Box sx={styles.dashboardSidebarContainer}>
      <Box
        sx={{
          ...styles.leftPane,
          width: theme.spacing(isCollapsed ? 9 : 43),
          gap: isCollapsed ? 0 : 3,
        }}
      >
        <Box sx={{ ...styles.searchContainer, ...(isCollapsed && { mb: 3 }) }}>
          {!isCollapsed && (
            <SearchInput
              inputProps={{
                placeholder: t("searchPlaceholder"),
                value: searchValue,
                endAdornment: searchValue ? (
                  <IconButton
                    onClick={handleSearchClearClick}
                    size="small"
                    sx={styles.clearIconButton}
                  >
                    <Tooltip title={t("common:clear")}>
                      <ClearSearchIcon />
                    </Tooltip>
                  </IconButton>
                ) : undefined,
                onChange: onSearchChange,
              }}
              sx={{ flex: 1 }}
            />
          )}

          <IconButton
            size="small"
            sx={{ ...styles.toggleButton, transform: `rotate(${isCollapsed ? "180deg" : "0deg"})` }}
            onClick={toggleSidebar}
            data-testid="sidebar-toggle"
          >
            <Tooltip title={isCollapsed ? t("common:open") : t("common:close")}>
              <ChevronLeftIcon color="primary" />
            </Tooltip>
          </IconButton>
        </Box>
        <>
          <Tooltip title={isCollapsed && t("startNewProject")}>
            <Button
              variant="contained"
              sx={{
                ...styles.startButton,
                minWidth: theme.spacing(isCollapsed ? 4 : 8),
                ...(isCollapsed && { mb: 3 }),
              }}
              onClick={handleNewProject}
            >
              {isCollapsed ? <AddCircleOutlineIcon /> : t("startNewProject")}
            </Button>
          </Tooltip>
          {isCollapsed && <Divider />}
        </>
        <Box sx={styles.listMenuContainer} ref={buttonRef}>
          <ProjectListMenu
            startIcon={
              <Box
                component="img"
                src={folderOpenUrl}
                sx={styles.folderOpenImg}
                alt="Folder Open"
              />
            }
            endIconOpen={<ExpandLess fontSize="medium" sx={styles.endIcon} />}
            endIconClosed={<ExpandMore fontSize="medium" sx={styles.endIcon} />}
            primaryText={t("selectProject")}
            secondaryText={selectedProject?.name || ""}
            menuItems={listProjectsData?.projects ?? []}
            onMenuItemClick={handleSelectProject}
            buttonProps={{
              color: "primary",
              variant: "outlined",
              fullWidth: true,
              sx: styles.projectListMenu,
            }}
            isCollapsed={isCollapsed}
          />
          {!isCollapsed && (
            <IconButton sx={styles.threeDotButton} onClick={handleClick}>
              <MoreHorizIcon color="primary" data-testid="MoreHorizIcon" />
            </IconButton>
          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  width: buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "auto",
                  marginTop: 2,
                },
              },
            }}
          >
            {menuOptions?.map((item: string, index: number) => (
              <MenuItem
                key={item}
                onClick={() => handleMenuOptionClick(item)}
                sx={{
                  borderBottom:
                    index < menuOptions.length - 1
                      ? `1px solid  ${theme.palette.grey[300]}`
                      : "none",
                  ...styles.menuOptions,
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box px={isCollapsed ? 1 : 2}>
          <List disablePadding>
            {getProjectData?.project?.rdts?.map((test, index) => (
              <React.Fragment key={index}>
                <TestListsItem
                  test={test}
                  isSelected={test.rdt_uid === selectedTest?.rdt_uid}
                  onSelectTest={handleSelectTest}
                  isCollapsed={isCollapsed}
                />
                {!(test.rdt_uid === selectedTest?.rdt_uid) && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardSidebar;

const styles = createStyleSheet({
  leftPane: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: theme.palette.common.white,
    transition: theme =>
      theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
  },
  searchContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1,
    p: 2,
    pb: 0,
  },
  startButton: {
    mx: 2,
  },
  toggleButton: {
    border: theme => `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
    transition: theme =>
      theme.transitions.create("transform", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
  },
  projectListMenu: {
    justifyContent: "space-between",
    alignItems: "center",
    gap: 0,
    border: theme => `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
    p: 2,
  },
  newTestLabsImg: { height: theme.spacing(3), objectFit: "contain" },
  folderOpenImg: { height: 20, objectFit: "contain" },
  dashboardSidebarContainer: { height: "100%" },
  clearIconButton: { marginRight: 1 },
  endIcon: { color: theme.palette.text.secondary },
  threeDotButton: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    border: "1px solid #ddd",
    mr: 1,
  },
  listMenuContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuOptions: {
    color: theme.palette.primary.main,
    padding: "10px",
  },
});
